import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  PlayArrow as VideoIcon,
  Article as ArticleIcon,
  Description as DocumentIcon,
  School as CourseIcon,
  MenuBook as BookIcon,
  Podcasts as PodcastIcon,
  Language as WebsiteIcon,
  Link as LinkIcon,
  AutoFixHigh as AutoFillIcon,
} from '@mui/icons-material';
import { CreateResourceForm, ResourceType } from '../../types/knowledge';

interface AddResourceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (resourceData: CreateResourceForm) => Promise<void>;
  nodeId: string;
  nodeName: string;
}

const resourceTypeIcons: Record<ResourceType, React.ReactElement> = {
  youtube_video: React.createElement(VideoIcon),
  article: React.createElement(ArticleIcon),
  document: React.createElement(DocumentIcon),
  course: React.createElement(CourseIcon),
  book: React.createElement(BookIcon),
  podcast: React.createElement(PodcastIcon),
  website: React.createElement(WebsiteIcon),
  other: React.createElement(LinkIcon),
};

const resourceTypeLabels: Record<ResourceType, string> = {
  youtube_video: 'YouTube Video',
  article: 'Article',
  document: 'Document',
  course: 'Course',
  book: 'Book',
  podcast: 'Podcast',
  website: 'Website',
  other: 'Other',
};

export function AddResourceModal({ open, onClose, onSubmit, nodeId, nodeName }: AddResourceModalProps) {
  const [formData, setFormData] = useState<CreateResourceForm>({
    node_id: nodeId,
    title: '',
    description: '',
    resource_type: 'youtube_video',
    url: '',
    thumbnail_url: '',
    duration: '',
    author: '',
    published_date: '',
    tags: [],
    is_featured: false,
    order_index: 0,
  });

  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof CreateResourceForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const extractYouTubeInfo = async (url: string) => {
    if (!url.includes('youtube.com') && !url.includes('youtu.be')) return;

    try {
      // Extract video ID
      let videoId = '';
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      }

      if (videoId) {
        // Generate thumbnail URL
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        setFormData(prev => ({
          ...prev,
          thumbnail_url: thumbnailUrl,
          resource_type: 'youtube_video'
        }));
      }
    } catch (err) {
      console.error('Error extracting YouTube info:', err);
    }
  };

  const handleUrlChange = (value: string) => {
    setFormData(prev => ({ ...prev, url: value }));
    if (value.includes('youtube.com') || value.includes('youtu.be')) {
      extractYouTubeInfo(value);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
      handleClose();
    } catch (err) {
      setError('Failed to add resource');
      console.error('Error adding resource:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      node_id: nodeId,
      title: '',
      description: '',
      resource_type: 'youtube_video',
      url: '',
      thumbnail_url: '',
      duration: '',
      author: '',
      published_date: '',
      tags: [],
      is_featured: false,
      order_index: 0,
    });
    setNewTag('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6">Add Resource</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Add educational resources to "{nodeName}"
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Resource Type */}
          <FormControl fullWidth>
            <InputLabel>Resource Type</InputLabel>
            <Select
              value={formData.resource_type}
              onChange={(e) => handleInputChange('resource_type', e.target.value as ResourceType)}
              label="Resource Type"
            >
              {Object.entries(resourceTypeLabels).map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {resourceTypeIcons[type as ResourceType]}
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
            placeholder="Enter resource title"
          />

          {/* URL */}
          <TextField
            fullWidth
            label="URL (Optional)"
            value={formData.url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Auto-fill from URL">
                    <IconButton size="small">
                      <AutoFillIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            placeholder="Brief description of the resource"
          />

          {/* Author */}
          <TextField
            fullWidth
            label="Author/Creator (Optional)"
            value={formData.author}
            onChange={(e) => handleInputChange('author', e.target.value)}
            placeholder="Name of the author or creator"
          />

          {/* Duration (for videos, podcasts, courses) */}
          {(formData.resource_type === 'youtube_video' || 
            formData.resource_type === 'podcast' || 
            formData.resource_type === 'course') && (
            <TextField
              fullWidth
              label="Duration (Optional)"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              placeholder="e.g., 10:30, 1h 30m, 2 hours"
            />
          )}

          {/* Published Date */}
          <TextField
            fullWidth
            label="Published Date (Optional)"
            type="date"
            value={formData.published_date}
            onChange={(e) => handleInputChange('published_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add a tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
                size="small"
              >
                Add
              </Button>
            </Box>
          </Box>

          {/* Featured Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.is_featured}
                onChange={(e) => handleInputChange('is_featured', e.target.checked)}
              />
            }
            label="Mark as featured resource"
          />

          {/* Thumbnail Preview */}
          {formData.thumbnail_url && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Thumbnail Preview
              </Typography>
              <Box
                component="img"
                src={formData.thumbnail_url}
                alt="Thumbnail"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title.trim()}
        >
          {loading ? 'Adding...' : 'Add Resource'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
